/**
 * Utilidad para detectar y manejar errores de Firestore "requires an index".
 * Cuando una query compuesta necesita un índice, Firestore lanza un error con un enlace
 * para crear el índice. Esta utilidad permite:
 * - Detectar si el error es por índice faltante
 * - Extraer el enlace de creación (si existe)
 * - Obtener un mensaje amigable para mostrar en UI
 * - Loggear el error completo en consola para que el admin pueda crear el índice
 */

const INDEX_ERROR_PATTERNS = [
  'requires an index',
  'index',
  'The query requires an index',
  'FAILED_PRECONDITION',
  'indexes'
];

const INDEX_URL_REGEX = /https:\/\/console\.firebase\.google\.com[^\s)]+/;

/**
 * Indica si el error de Firestore es por falta de índice compuesto.
 * @param {Error} error
 * @returns {boolean}
 */
export function isFirestoreIndexError(error) {
  if (!error || typeof error.message !== 'string') return false;
  const msg = (error.message || '').toLowerCase();
  const code = (error.code || '').toLowerCase();
  return (
    INDEX_ERROR_PATTERNS.some(p => msg.includes(p.toLowerCase())) ||
    code === 'failed_precondition'
  );
}

/**
 * Extrae la URL de creación de índice del mensaje de error (si existe).
 * @param {Error} error
 * @returns {string|null}
 */
export function getFirestoreIndexErrorUrl(error) {
  if (!error) return null;
  const str = String(error.message || '');
  const match = str.match(INDEX_URL_REGEX);
  return match ? match[0] : null;
}

/**
 * Obtiene información para log y UI del error de índice.
 * @param {Error} error
 * @param {Object} options
 * @param {string} [options.context] - Contexto opcional (ej: "SolicitudesRegistro.cargarSolicitudes")
 * @returns {{ userMessage: string, indexUrl: string|null, fullMessage: string }}
 */
export function getFirestoreIndexErrorInfo(error, options = {}) {
  const context = options.context || 'Firestore query';
  const indexUrl = getFirestoreIndexErrorUrl(error);
  const fullMessage = [
    `[Firestore Index Error] ${context}`,
    `Message: ${error?.message || 'Unknown'}`,
    `Code: ${error?.code || 'N/A'}`,
    indexUrl ? `Index creation link: ${indexUrl}` : ''
  ].filter(Boolean).join('\n');

  return {
    userMessage: 'Falta un índice en Firestore para este filtro u orden. Revisa la consola del navegador (F12) para ver el enlace de creación del índice.',
    indexUrl: indexUrl || null,
    fullMessage
  };
}

/**
 * Loggea el error de índice en consola y devuelve la info para UI.
 * Uso: const info = logFirestoreIndexError(err, { context: 'SolicitudesRegistro' });
 * @param {Error} error
 * @param {Object} options
 * @returns {{ userMessage: string, indexUrl: string|null }}
 */
export function logFirestoreIndexError(error, options = {}) {
  const info = getFirestoreIndexErrorInfo(error, options);
  console.error(info.fullMessage);
  if (info.indexUrl) {
    console.error('🔗 Crear índice (copiar enlace):', info.indexUrl);
  }
  return { userMessage: info.userMessage, indexUrl: info.indexUrl };
}
